import { addAdminMember, adminQueries, removeAdminMember, setAdminMemberAdmin } from "@jkast90/kast-common/admin-core";
import { hasGroup } from "@jkast90/kast-common/auth";
import { SLOW_CALL, api, apiRequest } from "@common/api";
import { ADMIN_GROUPS, session } from "../auth/session";
import type { Access, CoverSync as ApiCoverSync, SaveMeta } from "../types/api";

// Client for play-api's admin routes; the API refuses anyone outside the
// admin group, this only decides whether to show the page.

export type SaveFile = SaveMeta;

/** The signed-in account's sub and whether its token carries the admin group. */
export function signedInAdmin(): { sub: string | null; admin: boolean } {
  const { user } = session.getState();
  return { sub: user?.sub || null, admin: hasGroup(user, ADMIN_GROUPS) };
}

/** `outcome`: "cover" saved, "miss" (libretro has none) or "skip" (no console with art, or no game file inside). */
export type CoverSync = ApiCoverSync;

export const admin = {
  addMember: (email: string) => addAdminMember(apiRequest, "", email),
  removeMember: (sub: string) => removeAdminMember(apiRequest, "", sub),
  setAdmin: (sub: string, isAdmin: boolean) => setAdminMemberAdmin(apiRequest, "", sub, isAdmin),
  /** Whether every signed-in account may use cloud features, group or not. */
  access: (): Promise<Access> => api.get("/admin/access"),
  setAccess: (open: boolean): Promise<Access> => api.put("/admin/access", { body: { open } }),
  saves: (sub: string): Promise<SaveFile[]> => api.get("/admin/saves/{sub}", { params: { sub } }),
  deleteSave: async (sub: string, name: string): Promise<void> => {
    await api.delete("/admin/saves/{sub}/{name}", { params: { sub, name } });
  },
  /** How many saves and players' play times moved with it, and whether its cover did. */
  renameRom: (id: string, name: string) =>
    api.post("/admin/library/{id}/rename", { params: { id }, body: { name }, ...SLOW_CALL }),
  /** Finds one library game's box art and saves it as its cover. */
  syncCover: (id: string): Promise<CoverSync> => api.post("/admin/covers/{id}", { params: { id }, ...SLOW_CALL }),
  deleteRom: async (id: string): Promise<void> => {
    await api.delete("/library/{id}", { params: { id } });
  },
};

/** The deploys, lambdas, visitors (most recent first, at most 1000) and members, keyed under ["admin"]. */
export const adminQuery = adminQueries(apiRequest);

/** The logs (GET /admin/logs, newest first, at most LOG_LIMIT): a slow call, CloudWatch is read on the way. */
export const adminLogsQuery = adminQueries((path, init) => apiRequest(path, init, SLOW_CALL)).logs;
